/**
 * @file useSiteAccess.ts
 * @description Открыт ли сайт для покупателей — состояние паузы для плашки,
 * раздела «Доступ к сайту», «⋯» конструктора и окна публикации (phase19.16,
 * SB138; db-design-site-closed.md §6). Данные — `site-access-store.ts`
 * (одна запись на вкладку, по кабинету `contextKey`).
 *
 * Кто спрашивает сервер (`mode`):
 *  - `poll` — каркас кабинета магазина (один на вкладку): сразу, раз в 60 с
 *    (как `NAV_COUNTERS_POLL_MS`), при возврате во вкладку и отдельным
 *    таймером: ровно в `autoOpenAt` — перерисовка (слот плашки убирается
 *    без пустой полосы), через 2 с — перечитать состояние;
 *  - `load` — раздел «Доступ к сайту»: один запрос, если записи ещё нет;
 *  - `read` — только читает (меню «⋯», окно публикации): без лишних запросов.
 * После своих close/open/extend ответ кладётся сразу (`apply`); ответ
 * опроса, начатого до действия, отбрасывается (номер записи в хранилище). Время
 * считается от `serverTime` с поправкой на часы браузера (`serverNow`).
 */

import type { SiteAccessStateDto } from "@chaiberi/shared";
import { useCallback, useEffect, useState, useSyncExternalStore } from "react";

import { useCurrentContext } from "../../auth/hooks";
import { siteAccess } from "../../lib/api-site-access";
import {
  applySiteAccess,
  siteAccessEntry,
  siteAccessGeneration,
  siteAccessNow,
  siteAccessSeq,
  storeSiteAccess,
  storeSiteAccessError,
  subscribeSiteAccess,
  type SiteAccessEntry,
} from "../../lib/site-access/site-access-store";
import { NAV_COUNTERS_POLL_MS } from "../useNavCounters";

/** Период опроса — как у счётчиков меню. */
export const SITE_ACCESS_POLL_MS = NAV_COUNTERS_POLL_MS;
/** Запас после срока: сервер уже считает паузу закончившейся. */
export const SITE_ACCESS_AUTO_OPEN_GRACE_MS = 2000;
/** Возврат во вкладку не чаще — частые переключения не засыпают сервер. */
const RETURN_MIN_GAP_MS = 5000;
/** Предел `setTimeout` (≈ 24,8 сут.): дальше таймер сработал бы сразу. */
const MAX_TIMEOUT_MS = 2_147_483_647;

export type SiteAccessMode = "poll" | "load" | "read";

/** Один запрос на вкладку: поколение кэша и кабинет, для которых он начат. */
const inflight = new Map<string, Promise<void>>();

/** Перечитать состояние кабинета `key`; одновременные вызовы делят один запрос. */
export function loadSiteAccess(key: string): Promise<void> {
  const started = siteAccessGeneration();
  // Своё действие после старта запроса делает его ответ устаревшим (seq).
  const startedSeq = siteAccessSeq();
  const id = `${started}\u0000${startedSeq}\u0000${key}`;
  const running = inflight.get(id);
  if (running) return running;
  const request = siteAccess
    .state()
    .then((data) => storeSiteAccess(started, key, data, Date.now(), startedSeq))
    .catch(() => storeSiteAccessError(started, key, Date.now(), startedSeq))
    .finally(() => inflight.delete(id));
  inflight.set(id, request);
  return request;
}

export interface SiteAccessHandle {
  /** Запись своего кабинета (`null` — нет кабинета, не загружено). */
  entry: SiteAccessEntry | null;
  state: SiteAccessStateDto | null;
  /** Ни один запрос не удался (данных нет). */
  failed: boolean;
  /** Ещё не знаем: запрос не вернулся. */
  loading: boolean;
  /** «Сейчас» по часам сервера. */
  serverNow: () => Date;
  reload: () => Promise<void>;
  /** Положить ответ своей мутации. */
  apply: (state: SiteAccessStateDto) => void;
}

/** Ключ кабинета магазина вкладки; в кабинете платформы — `null`. */
export function useShopContextKey(): string | null {
  const ctx = useCurrentContext();
  return ctx?.contextType === "SHOP" && ctx.id ? ctx.id : null;
}

export function useSiteAccess(contextKey: string | null, mode: SiteAccessMode = "read"): SiteAccessHandle {
  const raw = useSyncExternalStore(subscribeSiteAccess, siteAccessEntry, () => null);
  const entry = contextKey && raw?.key === contextKey ? raw : null;
  const poll = mode === "poll" && contextKey !== null;
  const shopAuto = entry?.data?.shopClosure?.autoOpenAt ?? null;
  const platformAuto = entry?.data?.platformClosure?.autoOpenAt ?? null;
  const offset = entry?.offsetMs ?? 0;

  // Первый запрос: опрос — всегда, раздел — если записи своего кабинета нет.
  useEffect(() => {
    if (!contextKey || mode === "read") return;
    if (mode === "poll" || siteAccessEntry()?.key !== contextKey) void loadSiteAccess(contextKey);
  }, [contextKey, mode]);

  // Опрос раз в 60 с и возврат во вкладку.
  useEffect(() => {
    if (!poll || !contextKey) return;
    let lastFetch = Date.now();
    const load = () => {
      lastFetch = Date.now();
      void loadSiteAccess(contextKey);
    };
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, SITE_ACCESS_POLL_MS);
    const onVisibility = () => {
      if (document.visibilityState === "visible" && Date.now() - lastFetch >= RETURN_MIN_GAP_MS) load();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [poll, contextKey]);

  // Срок паузы (по часам сервера): ровно в `autoOpenAt` — перерисовать
  // (каркас убирает слот плашки без пустой полосы), через 2 с — перечитать.
  const [, setEdge] = useState(0);
  useEffect(() => {
    if (!poll || !contextKey) return;
    const times = [shopAuto, platformAuto].map((v) => (v ? Date.parse(v) : NaN)).filter(Number.isFinite);
    if (times.length === 0) return;
    const left = Math.min(...times) - (Date.now() + offset);
    const timers: number[] = [];
    if (left >= 0) timers.push(window.setTimeout(() => setEdge((n) => n + 1), Math.min(left, MAX_TIMEOUT_MS)));
    const reloadIn = left + SITE_ACCESS_AUTO_OPEN_GRACE_MS;
    if (reloadIn > 0) timers.push(window.setTimeout(() => void loadSiteAccess(contextKey), Math.min(reloadIn, MAX_TIMEOUT_MS)));
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [poll, contextKey, shopAuto, platformAuto, offset]);

  const reload = useCallback(() => (contextKey ? loadSiteAccess(contextKey) : Promise.resolve()), [contextKey]);
  const apply = useCallback(
    (state: SiteAccessStateDto) => {
      if (contextKey) applySiteAccess(contextKey, state);
    },
    [contextKey],
  );
  const serverNow = useCallback(() => siteAccessNow(entry), [entry]);

  return {
    entry,
    state: entry?.data ?? null,
    failed: !!entry?.error && !entry.data,
    loading: !!contextKey && mode !== "read" && !entry,
    serverNow,
    reload,
    apply,
  };
}
